import { cx } from './cx'

/**
 * Dedicated avatar swatches — deliberately darker than the design-system
 * tokens of the same hue (`--color-success`/`--color-warning`/
 * `--color-info`/`--color-accent-purple`) this palette started from. White
 * initials text on those *token* values only clears ~2.4–4.5:1 against
 * white (measured against the token hex values: success #16bc9c 2.41,
 * warning #f0811e 2.67, info #4b7ffc 3.66, accent-purple #9747ff 4.51),
 * every one of them under WCAG 1.4.3
 * AA's 4.5:1 minimum for this sub-18.66px bold text. Each swatch below is
 * tuned until white text clears >=4.5:1 while keeping a distinct hue
 * per slot. Not reusing the shared tokens directly here because they back
 * other UI (chip fills, borders) at their original, lighter value —
 * darkening those tokens globally would be a much bigger change.
 *
 * UID-393 — ONE SWATCH PER HUE FAMILY. This palette used to hold SIX
 * entries, THREE of which were the same blue family: `--color-brand-primary`
 * (#0f208b), `#1e40af` (darkened info) and `--color-accent-indigo`
 * (#19148e). Half the palette therefore read as "blue" to a user, so two
 * different people in the same avatar stack routinely looked like the same
 * colour — which defeats the only job a colour-coded avatar has. Both blue
 * DUPLICATES are removed and ONE red is added, giving exactly five
 * swatches across five distinct families: blue, purple, green/teal,
 * orange, red.
 *
 * That count and those families are the comp's (`Fictiv Platform.dc.html`
 * `_memColor` at `:8756`, feeding the card/row member stacks at `:8765`;
 * `_wsTint` at `:8771` is a second declaration of the SAME five values used
 * by the workspace-detail member list at `:9950`): teal #16BC9C, blue
 * #4B7FFC, purple #8b5cf6, orange #F0811E, red #F54A4A. Its RAW HEXES are
 * NOT adopted — white text on all five FAILS 4.5:1 (measured: 2.41, 3.66,
 * 4.23, 2.67, 3.52:1 respectively). So this palette matches the comp's
 * hue STRUCTURE while keeping the darkened, compliant values.
 *
 * White-text contrast, recomputed for UID-468 by the WCAG 2.x relative-
 * luminance formula this comment has always used (see `Avatar.test.tsx`,
 * which pins every one of these numbers so they cannot silently rot):
 * blue #195cfb 5.30:1, purple #7a43f5 5.32:1, green/teal #0f7a5e 5.30:1,
 * orange #9a4b00 6.21:1, red #b91c1c 6.47:1. The red is Tailwind
 * red-700, not red-600: red-600 (#dc2626) does technically pass at 4.83:1,
 * but that leaves only 0.33 of headroom over the floor, whereas every other
 * swatch here sits at 5.30:1 or better. red-700 keeps the palette's weakest
 * link consistent with the rest rather than parking one swatch on the line.
 *
 * UID-468 — BLUE AND PURPLE LIGHTENED TO THE PALETTE'S OWN FLOOR. Both sat
 * far above it (blue was `--color-brand-primary` #0f208b at 13.13:1, purple
 * #6d28d9 at 7.10:1) while green/teal defined the floor at 5.30:1. Blue and
 * purple were therefore the palette's two DARKEST swatches, and the only two
 * carrying real headroom to spend — the other three (5.30, 6.21, 6.47) all
 * sit within ~1.2 of the floor already. NOTE the comp's own slots for these
 * two are NOT its brightest (#4B7FFC 3.66:1, #8b5cf6 4.23:1 — its brightest
 * are teal 2.41 and orange 2.67); the reason to move blue and purple is the
 * headroom, not comp brightness. They are re-lightened toward the comp's
 * hues only as far as ~5.3:1, matching green rather than crossing it: the
 * whole palette now sits in a 5.30–6.47:1 band instead of a 5.30–13.13:1
 * one, so no swatch is conspicuously darker than its neighbours and none is
 * parked on the 4.5:1 line.
 *
 * BOTH NEW VALUES ARE LITERALS, deliberately, and BOTH gave up a token to
 * become so — record this or someone will "fix" it back. Blue was
 * `--color-brand-primary`; navy is the app's brand/CTA colour (it is also
 * the topbar cart badge's fill), so as an avatar it read as chrome rather
 * than as one person's identity. Purple was byte-identical to
 * `--color-accent-purple-text` (#6d28d9). The obvious token substitutes are
 * REJECTED on hue, not on contrast: `--color-info-text` #1d4fd1 sits at hue
 * 223.3 whereas the comp's blue is 222.4, and the chosen #195cfb holds
 * 222.2 — under a quarter-degree off the comp, where the token is a full
 * degree off. #7a43f5 (258.5) tracks the comp's purple (258.3) the same way.
 * Matching the comp's hue is this palette's whole job, so bespoke literals
 * beat near-miss tokens here. The hue-separation test still governs how
 * close blue and purple may sit (they clear the 25-degree bar at 36.3).
 *
 * SEEDING IS HASH-BASED, NOT POSITION-INDEXED (UID-393 decision, recorded
 * because the ticket asked for the call explicitly rather than a silent
 * default). `colorFor(name)` hashes the person's name; the comp instead
 * position-indexes (`_memColor=(i)=>[...][i%5]`). The comp is shaped that
 * way only because its mock data has no stable per-member identity to hash
 * — index is all it has. This component's own contract, documented on
 * `Avatar` below, is that "the same person always renders the same colour
 * across screens", and ONLY hashing delivers that: under `i % 5` the same
 * person would change colour whenever their position in a list moved, and
 * would differ between the grid card, the list row and the detail header,
 * which each slice the roster differently. Position-indexing would break a
 * shipped guarantee for no benefit, so it is deliberately NOT adopted.
 *
 * Exported ONLY so `Avatar.test.tsx` can assert the palette's size, hue
 * distinctness and per-swatch contrast directly. Not for use as a general
 * colour source — read a token from `tokens.css` instead.
 */
export const AVATAR_PALETTE = [
  '#195cfb', // UID-468 lightened     — blue         5.30:1
  '#7a43f5', // UID-468 lightened     — purple       5.32:1
  '#0f7a5e', // darkened success      — green/teal   5.30:1
  '#9a4b00', // darkened warning      — orange       6.21:1
  '#b91c1c', // UID-393 red-700       — red          6.47:1
] as const

function colorFor(seed: string): string {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
  }
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length] as string
}

/** Only `https:` URLs are allowed into `<img src>` (security obs #2) — a
 * `data:`/`http:`/`javascript:` (or any other scheme) avatarUrl falls back
 * to the initials avatar instead of being rendered, since an unvalidated
 * scheme from a future real backend would be a minor exfiltration/tracking
 * vector. Relative/protocol-relative URLs (no parseable scheme) also fall
 * back rather than being guessed at. */
function isSafeAvatarUrl(url: string): boolean {
  try {
    return new URL(url).protocol === 'https:'
  } catch {
    return false
  }
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase()
}

export interface AvatarProps {
  readonly name: string
  readonly avatarUrl?: string | null
  readonly size?: number
  readonly className?: string
}

/** Initials-on-color avatar (components.md "Avatar" primitive). Renders a
 * real `<img>` with `alt` when `avatarUrl` is present; otherwise a
 * deterministically-colored initials circle so the same person always
 * renders the same color across screens. */
export function Avatar({ name, avatarUrl, size = 32, className }: AvatarProps) {
  const classes = cx('avatar', className)
  const style = { width: size, height: size, fontSize: size * 0.4 }

  if (avatarUrl && isSafeAvatarUrl(avatarUrl)) {
    return <img src={avatarUrl} alt={name} className={classes} style={style} />
  }

  return (
    <span
      className={classes}
      style={{ ...style, background: colorFor(name) }}
      role="img"
      aria-label={name}
    >
      {initialsOf(name)}
    </span>
  )
}

export interface AvatarStackProps {
  readonly people: readonly {
    readonly id: string
    readonly name: string
    readonly avatarUrl?: string | null
  }[]
  readonly max?: number
  readonly size?: number
  /**
   * UID-375 — overrides the inferred total when `people` is a truncated
   * preview rather than the full roster (e.g.
   * `WorkspaceSummary.membersPreview`, capped at 3 server-side by the
   * UID-374 contract). When provided, overflow is `totalCount -
   * visible.length` and the accessible group label announces this total
   * instead of `people.length`. A `totalCount` at or below `visible.length`
   * (zero or negative overflow) renders no badge — not via a clamp here, but
   * because the pre-existing `overflow > 0` guard below already excludes
   * both cases; there is nothing left for this prop to additionally guard
   * against. Omitted — as both `WorkspacePage.tsx` callers do, passing the
   * full unbounded `workspace.members` — behavior is byte-for-byte unchanged
   * from before this prop existed: total falls back to `people.length`.
   */
  readonly totalCount?: number
}

/** Overlapping avatar stack with a `+N others` overflow indicator
 * (components.md: `margin-right:-7px`, 2px white border). */
export function AvatarStack({ people, max = 4, size = 28, totalCount }: AvatarStackProps) {
  const visible = people.slice(0, max)
  const total = totalCount ?? people.length
  const overflow = total - visible.length

  return (
    <div
      className="avatar-stack"
      // This is a generic ARIA "group" of avatar images (review
      // Optional-S) — not a form control group, so <fieldset> would be
      // the wrong element.
      // biome-ignore lint/a11y/useSemanticElements: see comment above.
      role="group"
      aria-label={`${total} member${total === 1 ? '' : 's'}`}
    >
      {visible.map((person) => (
        <Avatar
          key={person.id}
          name={person.name}
          avatarUrl={person.avatarUrl ?? null}
          size={size}
        />
      ))}
      {overflow > 0 && (
        <span
          className="avatar avatar-overflow"
          style={{ width: size, height: size, fontSize: size * 0.35 }}
        >
          +{overflow}
        </span>
      )}
    </div>
  )
}
